import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ApiRequest } from '@/utils/axios';
import { useSubscription } from '@/utils/hooks/use-subscription';
import { IJackpotHistoryGame } from '@/utils/types/IJackpotHistoryGame';
import { IGameItem } from '@/utils/types/Items';
import { itemsValue } from '@/utils/itemInfo';
import { getColor, getLevelColor } from '@/utils/colors';
import AbstractAvatar from '@/components/Avatars/AbstractAvatar';
import AdminAvatarMenu from '@/components/Avatars/AdminAvatarMenu';
import LevelBadge from '@/components/Levels/LevelBadge';
import SkinsRow from '@/components/Jackpot/Game/SkinsRow';

const JackpotHistory = () => {
    const [games, setGames] = useState<IJackpotHistoryGame[]>([]);

    async function getHistory() {
        const {
            data: {
                response: { history },
            },
        } = await ApiRequest('/jackpot/history/all');
        setGames(history);
    }

    useEffect(() => {
        getHistory().then();
    }, []);

    useSubscription('jackpotFinished', () => {
        setTimeout(() => getHistory(), 11000);
    }).then();

    return (
        <section className="surface mt-3 p-3.5 px-4 flex flex-col gap-3.5">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2.5 text-[15px] font-bold tracking-[0.02em]">
                    <span
                        className="w-1 h-[18px] rounded-sm"
                        style={{
                            background: 'linear-gradient(#8b6dff,#3ad6ff)',
                            boxShadow: '0 0 12px #8b6dff',
                        }}
                    />
                    Game History
                    <span className="text-text_3 font-medium text-[13px]">
                        · all rounds verified
                    </span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-fl bg-layer_2 border border-stroke font-semibold text-[12.5px] text-text_3 tabular-nums">
                    Last 24h · {games.length} rounds
                </div>
            </div>

            <div className="flex flex-col gap-3.5">
                {games.map((game) => (
                    <GameBlock key={game.id} game={game} />
                ))}
            </div>
        </section>
    );
};

const CoinIcon = ({ size = 14 }: { size?: number }) => (
    <span
        className="rounded-full grid place-items-center shrink-0"
        style={{
            width: size,
            height: size,
            background: 'radial-gradient(circle at 35% 30%, #ffe08a, #ffc24d)',
            boxShadow: '0 0 12px rgba(255,194,77,0.7)',
        }}
    >
        <span
            className="font-black leading-none"
            style={{
                fontSize: size * 0.6,
                color: '#6a4500',
                textShadow: '0 1px 0 rgba(255,255,255,0.45)',
            }}
        >
            $
        </span>
    </span>
);

// Slow-falling confetti for the winner cell — a handful of CSS-animated pieces.
const CONFETTI_COLORS = ['#ffc24d', '#ff5fae', '#3ad6ff', '#46e08a', '#8b6dff', '#ffffff'];
const Confetti = () => (
    <div className="jp-confetti">
        {Array.from({ length: 14 }).map((_, i) => {
            const round = i % 5 === 0;
            return (
                <i
                    key={i}
                    style={{
                        left: `${4 + i * 6.8}%`,
                        width: round ? 6 : 4 + (i % 3),
                        height: round ? 6 : 9 + (i % 3),
                        borderRadius: round ? '50%' : '1px',
                        background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                        boxShadow: `0 0 6px -1px ${CONFETTI_COLORS[i % CONFETTI_COLORS.length]}`,
                        animationDuration: `${3.8 + (i % 5) * 0.7}s`,
                        animationDelay: `${-(i * 0.43).toFixed(2)}s`,
                        ['--dx' as string]: `${-15 + i * 2.3}px`,
                        ['--spin' as string]: `${360 + (i % 4) * 170}deg`,
                    }}
                />
            );
        })}
    </div>
);

const HeaderCol = ({
    label,
    children,
    className = '',
    color,
}: {
    label: string;
    children: React.ReactNode;
    className?: string;
    color?: string;
}) => (
    <div className={`flex flex-col gap-1 min-w-0 ${className}`}>
        <span className="text-[10px] uppercase tracking-[0.12em] text-text_4 font-semibold">
            {label}
        </span>
        <span
            className="text-[13px] font-bold tabular-nums truncate"
            style={color ? { color, textShadow: `0 0 10px ${color}66` } : undefined}
        >
            {children}
        </span>
    </div>
);

const GameBlock = ({ game }: { game: IJackpotHistoryGame }) => {
    const allSkins: IGameItem[] = Object.values(game.skins).flat();
    const totalPrize = itemsValue(allSkins);

    const players = game.users.map((u) => ({
        ...u,
        skins: game.skins[u.id] ?? [],
    }));
    const winnerIdx = players.findIndex((p) => p.id === game.winnerUserId);
    const orderedPlayers =
        winnerIdx >= 0
            ? [players[winnerIdx], ...players.filter((_, i) => i !== winnerIdx)]
            : players;
    const winner = winnerIdx >= 0 ? players[winnerIdx] : undefined;

    return (
        <div
            className="jp-history-card border border-stroke rounded-fl-md overflow-hidden"
            style={{
                background: 'rgba(139,109,255,0.04)',
                boxShadow: 'inset 0 0 26px rgba(139,109,255,0.05)',
            }}
        >
            {/* round meta + actions */}
            <div
                className="grid grid-cols-[0.5fr_0.5fr_1.25fr_1.25fr_auto] max-md:grid-cols-2 items-center gap-x-4 gap-y-3 px-[18px] py-3 border-b border-stroke"
                style={{
                    background:
                        'linear-gradient(90deg, rgba(139,109,255,0.14), rgba(58,214,255,0.06))',
                }}
            >
                <HeaderCol label="Round ID" color="#8b6dff">
                    #{game.id}
                </HeaderCol>
                <HeaderCol label="Percent" color="#46e08a">
                    {game.chancePercent}%
                </HeaderCol>
                <HeaderCol label="Secret" color="#3ad6ff">
                    {game.secret}
                </HeaderCol>
                <HeaderCol label="Hash" color="#3ad6ff">
                    {game.hash}
                </HeaderCol>

                <div className="flex items-center gap-2.5 justify-self-end max-md:col-span-2 max-md:justify-self-start flex-wrap">
                    <Link
                        href={`/fairplay?game=jackpot&hash=${game.hash}&secret=${game.secret}`}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-fl text-[12.5px] font-bold tracking-[0.02em] transition-all hover:brightness-110"
                        style={{
                            color: '#3ad6ff',
                            background: 'rgba(58,214,255,0.1)',
                            border: '1px solid rgba(58,214,255,0.4)',
                            boxShadow: '0 0 16px -5px #3ad6ff, inset 0 0 12px -6px #3ad6ff',
                        }}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-3.5 h-3.5"
                        >
                            <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" />
                            <path d="M9 12l2 2 4-4" />
                        </svg>
                        Provably Fair
                    </Link>
                    {winner && (
                        <div
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-fl font-bold text-[12.5px] whitespace-nowrap"
                            style={{
                                color: '#46e08a',
                                background: 'rgba(70,224,138,0.12)',
                                border: '1px solid rgba(70,224,138,0.4)',
                                boxShadow: '0 0 18px -4px #46e08a',
                            }}
                        >
                            🏆 {winner.name} won ${totalPrize.toFixed(2)}
                        </div>
                    )}
                </div>
            </div>

            {/* players — two per row */}
            <div className="grid grid-cols-2 max-md:grid-cols-1 gap-2.5 p-2.5">
                {orderedPlayers.map((p) => {
                    const subtotal = itemsValue(p.skins as IGameItem[]);
                    const chance = totalPrize > 0 ? (subtotal / totalPrize) * 100 : 0;
                    const isWinner = p.id === game.winnerUserId;
                    const color =
                        getColor((game.colors?.[p.id] as number) ?? p.color) ||
                        'rgba(255,255,255,0.10)';
                    const lvl = Math.floor((p.experience ?? 0) / 100);
                    const skins = p.skins as IGameItem[];
                    const skinWord = skins.length === 1 ? 'skin' : 'skins';

                    return (
                        <div
                            key={p.id}
                            style={
                                {
                                    ['--accent' as string]: color,
                                    ...(isWinner
                                        ? {
                                              isolation: 'isolate',
                                              background: `linear-gradient(100deg, color-mix(in srgb, ${color} 26%, transparent), color-mix(in srgb, ${color} 6%, transparent))`,
                                              borderColor: color,
                                              boxShadow: `0 0 28px -8px ${color}, inset 0 0 24px -8px ${color}`,
                                          }
                                        : {}),
                                } as React.CSSProperties
                            }
                            className={`relative grid grid-cols-[max-content_minmax(0,1fr)_auto] max-md:grid-cols-[max-content_1fr] items-center gap-3 max-md:gap-2.5 py-3 pl-4 pr-3.5 rounded-fl border overflow-hidden ${
                                isWinner
                                    ? 'jp-winner-sparkle'
                                    : 'border-stroke bg-white/[0.025] hover:border-stroke_strong hover:[box-shadow:0_0_24px_-6px_var(--accent)] transition-all'
                            }`}
                        >
                            {isWinner && <Confetti />}

                            {/* left accent bar */}
                            <span
                                className="absolute left-0 top-0 bottom-0 w-1 rounded-l-fl"
                                style={{ background: color, boxShadow: `0 0 16px ${color}`, zIndex: 1 }}
                            />

                            {/* avatar + meta */}
                            <div className="flex items-center gap-3 min-w-0 relative z-[1]">
                                <div className="relative w-12 h-12 shrink-0">
                                    <AdminAvatarMenu userId={p.id} userName={p.name}>
                                        <AbstractAvatar
                                            seed={p.name || String(p.id)}
                                            src={p.avatar}
                                            size={48}
                                            square
                                        />
                                    </AdminAvatarMenu>
                                    <div className="absolute -right-1.5 -bottom-1.5">
                                        <LevelBadge level={lvl} color={getLevelColor(lvl)} size="sm" variant="solid" />
                                    </div>
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[15px] font-bold text-white truncate max-w-[150px]">
                                            {p.name}
                                        </span>
                                        {isWinner && <span className="jp-win-flag">👑 Winner</span>}
                                    </div>
                                    <div className="text-xs text-text_3 mt-1">
                                        <b style={{ color }}>{skins.length}</b> {skinWord} ·{' '}
                                        {chance.toFixed(2)}%
                                    </div>
                                </div>
                            </div>

                            {/* skins — fit as many as the cell allows, +N for the rest */}
                            <SkinsRow skins={skins} tile={42} radius={9} className="relative z-[1]" mobileHidden={false} />

                            {/* value + chance */}
                            <div className="text-right shrink-0 relative z-[1] max-md:col-span-2">
                                <div className="flex items-center justify-end gap-1.5 font-extrabold text-[15px] text-accent_5 whitespace-nowrap">
                                    <CoinIcon size={14} />
                                    {subtotal.toFixed(2)}
                                </div>
                                <div
                                    className={`text-xs mt-1 font-semibold ${
                                        isWinner ? '' : 'text-text_3'
                                    }`}
                                    style={isWinner ? { color } : undefined}
                                >
                                    {chance.toFixed(2)}%
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default JackpotHistory;
